import React from 'react';
import { styled } from '@stitches/react';
import { useTranslation } from 'react-i18next';
import { useLanguage, LanguageType } from '../../contexts/LanguageContext';

// 语言切换器容器样式
const SwitcherContainer = styled('div', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  padding: 'var(--spacing-xs) var(--spacing-sm)',
  borderRadius: 'var(--border-radius-sm)',
  transition: 'all var(--transition-fast)',
  
  '&:hover': {
    color: 'var(--primary-color)',
    backgroundColor: 'var(--background-secondary)'
  },
  
  variants: {
    variant: {
      default: {
        fontSize: 'var(--font-size-sm)'
      },
      small: {
        fontSize: 'var(--font-size-xs)',
        padding: 'var(--spacing-xs)'
      },
      large: {
        fontSize: 'var(--font-size-md)',
        padding: 'var(--spacing-sm) var(--spacing-md)'
      }
    }
  },
  
  defaultVariants: {
    variant: 'default'
  }
});

// 语言图标样式
const LanguageIcon = styled('span', {
  marginRight: 'var(--spacing-xs)',
  fontSize: 'var(--font-size-md)'
});

// 组件属性接口
interface LanguageSwitcherProps {
  variant?: 'default' | 'small' | 'large';
  showIcon?: boolean;
  className?: string;
}

/**
 * 语言切换器组件
 * 提供切换应用语言的功能
 */
const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({ 
  variant = 'default', 
  showIcon = true,
  className
}) => {
  const { t } = useTranslation();
  const { language, toggleLanguage } = useLanguage();
  
  // 获取当前显示的语言文本
  const getLanguageText = () => {
    const languageOrder: LanguageType[] = ['zh', 'en', 'ru', 'ur', 'ko'];
    const currentIndex = languageOrder.indexOf(language);
    const nextIndex = (currentIndex + 1) % languageOrder.length;
    const nextLanguage = languageOrder[nextIndex];
    
    return t(`header.language.${nextLanguage}`);
  };
  
  return (
    <SwitcherContainer 
      variant={variant} 
      onClick={toggleLanguage}
      className={className}
      title={t('common.switchLanguage', '切换语言')}
      role="button"
      aria-label={t('common.switchLanguage', '切换语言')}
    >
      {showIcon && <LanguageIcon>🌐</LanguageIcon>}
      {getLanguageText()}
    </SwitcherContainer>
  );
};

export default LanguageSwitcher; 